import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as newsletterApi from "@/services/newsletterApi";

export const newsletterKeys = {
  all: ["newsletter"] as const,
  submissions: (params?: newsletterApi.ListNewsletterSubmissionsParams) =>
    [...newsletterKeys.all, "submissions", params ?? {}] as const,
  issues: () => [...newsletterKeys.all, "issues"] as const,
  issue: (issueId: number | null | undefined) =>
    [...newsletterKeys.all, "issues", issueId] as const,
};

export function useNewsletterSubmissions(
  params?: newsletterApi.ListNewsletterSubmissionsParams,
) {
  return useQuery({
    queryKey: newsletterKeys.submissions(params),
    queryFn: () => newsletterApi.listNewsletterSubmissions(params),
  });
}

export function usePatchNewsletterSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      ...patch
    }: {
      submissionId: number;
    } & newsletterApi.PatchNewsletterSubmissionRequest) =>
      newsletterApi.patchNewsletterSubmission(submissionId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.submissions() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues() });
    },
  });
}

export function useNewsletterIssues() {
  return useQuery({
    queryKey: newsletterKeys.issues(),
    queryFn: newsletterApi.listNewsletterIssues,
  });
}

export function useNewsletterIssue(issueId: number | null | undefined) {
  return useQuery({
    queryKey: newsletterKeys.issue(issueId),
    queryFn: () => newsletterApi.getNewsletterIssue(issueId!),
    enabled: issueId != null,
  });
}

export function useCreateNewsletterIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: newsletterApi.createNewsletterIssue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues() });
    },
  });
}

export function usePatchNewsletterIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      issueId,
      ...patch
    }: { issueId: number } & newsletterApi.PatchNewsletterIssueRequest) =>
      newsletterApi.patchNewsletterIssue(issueId, patch),
    onSuccess: (_updated, { issueId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues() });
      queryClient.invalidateQueries({
        queryKey: newsletterKeys.issue(issueId),
      });
    },
  });
}

export function useRenderNewsletterIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: newsletterApi.renderNewsletterIssue,
    onSuccess: (_result, issueId) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues() });
      queryClient.invalidateQueries({
        queryKey: newsletterKeys.issue(issueId),
      });
    },
  });
}

export function useTestSendNewsletterIssue() {
  return useMutation({
    mutationFn: ({
      issueId,
      recipientEmail,
    }: {
      issueId: number;
      recipientEmail: string;
    }) => newsletterApi.testSendNewsletterIssue(issueId, recipientEmail),
  });
}

export function useSendNewsletterIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: newsletterApi.sendNewsletterIssue,
    onSuccess: (_result, issueId) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues() });
      queryClient.invalidateQueries({
        queryKey: newsletterKeys.issue(issueId),
      });
    },
  });
}
